import { Temporal } from '@js-temporal/polyfill';
import type { CalendarEvent, EventInput, EventOccurrence } from '../shared/contracts';

export type RecurrenceFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
export type RecurrenceEnd = 'never' | 'count' | 'until';

export function eventInput(event: CalendarEvent): EventInput {
  return {
    calendarId: event.calendarId,
    title: event.title,
    start: event.start,
    end: event.end,
    allDay: event.allDay,
    timezone: event.timezone,
    physicalLocation: event.physicalLocation,
    virtualLocation: event.virtualLocation,
    summary: event.summary,
    page: event.page,
    status: event.status,
    recurrence: event.recurrence,
    reminderMinutes: event.reminderMinutes || [],
  };
}

export function seriesMutationInput(
  master: CalendarEvent,
  occurrence: EventOccurrence,
  moved: EventInput,
): EventInput {
  const input = eventInput(master);
  if (moved.allDay) {
    const oldStart = Temporal.PlainDate.from(occurrence.start.slice(0, 10));
    const oldEnd = Temporal.PlainDate.from(occurrence.end.slice(0, 10));
    input.start = Temporal.PlainDate.from(master.start)
      .add(oldStart.until(Temporal.PlainDate.from(moved.start)))
      .toString();
    input.end = Temporal.PlainDate.from(master.end)
      .add(oldEnd.until(Temporal.PlainDate.from(moved.end)))
      .toString();
  } else {
    const oldStart = Temporal.PlainDateTime.from(
      canonicalTime(occurrence.start, false, occurrence.timezone),
    );
    const oldEnd = Temporal.PlainDateTime.from(
      canonicalTime(occurrence.end, false, occurrence.timezone),
    );
    input.start = Temporal.PlainDateTime.from(master.start)
      .add(oldStart.until(Temporal.PlainDateTime.from(moved.start), { largestUnit: 'days' }))
      .toString({ smallestUnit: 'second' });
    input.end = Temporal.PlainDateTime.from(master.end)
      .add(oldEnd.until(Temporal.PlainDateTime.from(moved.end), { largestUnit: 'days' }))
      .toString({ smallestUnit: 'second' });
  }
  return input;
}

function canonicalTime(value: string, allDay: boolean, timezone: string) {
  if (allDay) return value.slice(0, 10);
  const local = /(?:Z|[+-]\d\d:\d\d)$/.test(value)
    ? Temporal.Instant.from(value).toZonedDateTimeISO(timezone).toPlainDateTime()
    : Temporal.PlainDateTime.from(value);
  return local.toString({ smallestUnit: 'second' });
}

export function calendarMutationInput(
  event: EventOccurrence,
  start: string,
  end: string,
  allDay: boolean,
): EventInput {
  return {
    ...eventInput(event),
    start: canonicalTime(start, allDay, event.timezone),
    end: canonicalTime(end, allDay, event.timezone),
    allDay,
  };
}

function recurrenceParts(rule: string | null) {
  return (rule || '')
    .split(';')
    .filter(Boolean)
    .map((part) => {
      const separator = part.indexOf('=');
      return [part.slice(0, separator), part.slice(separator + 1)] as const;
    });
}

export function recurrenceFields(rule: string | null) {
  const parts = new Map(recurrenceParts(rule));
  const count = Number(parts.get('COUNT') || 1);
  const until = parts.get('UNTIL') || '';
  return {
    frequency: (parts.get('FREQ') || 'WEEKLY') as RecurrenceFrequency,
    interval: Number(parts.get('INTERVAL') || 1),
    weekdays: (parts.get('BYDAY') || '').split(',').filter(Boolean),
    end: (parts.has('COUNT') ? 'count' : parts.has('UNTIL') ? 'until' : 'never') as RecurrenceEnd,
    count,
    until: until ? `${until.slice(0, 4)}-${until.slice(4, 6)}-${until.slice(6, 8)}` : '',
  };
}

export function updateRecurrence(
  rule: string | null,
  change: {
    frequency?: RecurrenceFrequency;
    interval?: number;
    weekdays?: string[];
    end?: RecurrenceEnd;
    count?: number;
    until?: string;
    allDay?: boolean;
  },
) {
  const original = new Map(recurrenceParts(rule));
  const fields = { ...recurrenceFields(rule), ...change };
  const controlled = new Set(['FREQ', 'INTERVAL', 'BYDAY', 'COUNT', 'UNTIL']);
  const preserved = recurrenceParts(rule).filter(([key]) => !controlled.has(key));
  const parts: Array<readonly [string, string]> = [['FREQ', fields.frequency]];
  if (fields.interval > 1) parts.push(['INTERVAL', String(fields.interval)]);
  const keepOriginalWeekdays = change.frequency === undefined && change.weekdays === undefined;
  const weekdays = keepOriginalWeekdays ? original.get('BYDAY') : fields.weekdays.join(',');
  if (weekdays && (fields.frequency === 'WEEKLY' || keepOriginalWeekdays))
    parts.push(['BYDAY', weekdays]);
  parts.push(...preserved);
  if (fields.end === 'count') parts.push(['COUNT', String(fields.count)]);
  if (fields.end === 'until' && fields.until) {
    const keepOriginalUntil = change.end === undefined && change.until === undefined;
    const compact = fields.until.replaceAll('-', '');
    parts.push([
      'UNTIL',
      keepOriginalUntil && original.has('UNTIL')
        ? original.get('UNTIL')!
        : fields.allDay
          ? compact
          : `${compact}T235959`,
    ]);
  }
  return parts.map(([key, value]) => `${key}=${value}`).join(';');
}
